import React, { type FunctionComponent, useMemo } from 'react'
import { useDomainMessages } from '../../../utils/domain/intl.ts'
import {
  Input,
  PropertyContainer,
  PropertyRow,
  PropertyTitle,
} from '../../shared-styled-components/SharedStyledComponents'
import {
  AttributeManagementNextStepTypeEnum,
  type PipelineStep,
  type PipelineStepNextStepsRule,
} from '../../../types/attributeManagement.types.ts'

interface Props {
  step: PipelineStep
  onChange: (step: PipelineStep) => void
}

const MAX_NUM = 10
const MIN_NUM = 1

export const PipelineFormStepControlRegex: FunctionComponent<Props> = ({ step, onChange }) => {
  const i18n = useDomainMessages({
    numberOfMatches:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.controlRegex.numberOfMatches',
    fieldMatch:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.controlRegex.fieldMatch',
    regex:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.controlRegex.regex',
  })

  const nextStepsRule: PipelineStepNextStepsRule = useMemo(
    () => ({
      type: AttributeManagementNextStepTypeEnum.RegexRules,
      campField: step?.nextStepsRule?.campField ?? '',
      matches: step?.nextStepsRule?.matches ?? [{ regex: '', nextPipelineStep: '' }],
      noMatchNextPipelineStep: undefined,
      possibleNextSteps: step?.nextStepsRule?.possibleNextSteps ?? [],
    }),
    [
      step?.nextStepsRule?.campField,
      step?.nextStepsRule?.matches,
      step?.nextStepsRule?.possibleNextSteps,
    ]
  )

  return (
    <>
      <PropertyContainer style={{ flexDirection: 'column' }}>
        <PropertyRow>
          <PropertyTitle>{i18n.numberOfMatches}</PropertyTitle>
          <Input
            type={'number'}
            value={nextStepsRule.matches?.length ?? 1}
            onChange={e => {
              const amount = Math.min(MAX_NUM, Math.max(MIN_NUM, Number.parseInt(e.target.value)))

              const matches: { regex: string; nextPipelineStep: string }[] = []
              for (let i = 0; i < amount; i++) {
                matches.push(
                  nextStepsRule?.matches?.[i] ?? {
                    regex: '',
                    nextPipelineStep: '',
                  }
                )
              }

              const existingPossibleNextSteps = step?.nextStepsRule?.possibleNextSteps ?? []
              const possibleNextSteps = Array(amount)
                .fill('')
                .map((_, index) => existingPossibleNextSteps[index] ?? '')

              onChange({
                ...step,
                nextStepsRule: {
                  ...nextStepsRule,
                  matches: matches,
                  possibleNextSteps: possibleNextSteps,
                },
              })
            }}
          />
        </PropertyRow>
        <PropertyRow>
          <PropertyTitle>{i18n.fieldMatch}</PropertyTitle>
          <Input
            type={'text'}
            error={!nextStepsRule?.campField?.trim()}
            value={nextStepsRule?.campField ?? ''}
            onChange={e => {
              onChange({
                ...step,
                nextStepsRule: {
                  ...nextStepsRule,
                  campField: e.target.value,
                },
              })
            }}
          />
        </PropertyRow>
        {(nextStepsRule.matches ?? []).map((matches, index) => (
          <PropertyRow key={`${index}`}>
            <PropertyTitle>
              {i18n.regex} {index + 1}:
            </PropertyTitle>
            <Input
              type={'text'}
              error={!matches.regex?.trim()}
              value={matches.regex ?? ''}
              onChange={e => {
                if (!nextStepsRule.matches) return
                nextStepsRule.matches[index].regex = e.target.value
                onChange({
                  ...step,
                  nextStepsRule: nextStepsRule,
                })
              }}
            />
          </PropertyRow>
        ))}
      </PropertyContainer>
    </>
  )
}
